import { useRef } from "react";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import { Textarea } from "../../ui/textarea";
import { Switch } from "../../ui/switch";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../ui/select";
import {
  Plus,
  X,
  Upload,
  RefreshCw,
  CheckSquare,
  Square,
  AlertCircle,
} from "lucide-react";
import TintedImage from "./TintedImage";
import { getImageUrl } from "./configuratorConstants";

const OptionDialog = ({
  open,
  onOpenChange,
  isDark,
  editingOption,
  optionForm,
  setOptionForm,
  editingOptionStepId,
  steps,
  uploading,
  onSave,
  onMainImageUpload,
  onExtraImageUpload,
  onSvgUpload,
  removeImage,
  addDependency,
  updateDependency,
  removeDependency,
  getAvailableDependencySteps,
}) => {
  const optionMainImageInputRef = useRef(null);
  const optionExtraImageInputRef = useRef(null);
  const optionSvgInputRef = useRef(null);

  const parentStep = steps.find((s) => s.id === editingOptionStepId);
  const isFrameColor = parentStep?.type === "frame-color";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className={`sm:max-w-lg max-h-[85vh] overflow-y-auto ${isDark ? "bg-[#141414] border-white/[0.06]" : "bg-white"}`}
      >
        <DialogHeader>
          <DialogTitle className={isDark ? "text-white" : ""}>
            {editingOption ? "Optie Bewerken" : "Nieuwe Optie"}
            {isFrameColor && (
              <span
                className={`text-xs font-normal ml-2 ${isDark ? "text-gray-500" : "text-gray-400"}`}
              >
                Frame Kleur
              </span>
            )}
          </DialogTitle>
          <DialogDescription>
            {isFrameColor
              ? editingOption
                ? "Pas de frame kleur optie aan."
                : "Voeg een frame kleur optie toe met afbeelding en standaardkleur."
              : editingOption
                ? "Pas de details van deze optie aan."
                : "Voeg een optie toe met afbeelding, prijs en kleur."}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          {/* Name */}
          <div className="space-y-2">
            <Label className={isDark ? "text-gray-300" : ""}>Naam *</Label>
            <Input
              value={optionForm.name}
              onChange={(e) =>
                setOptionForm((p) => ({ ...p, name: e.target.value }))
              }
              placeholder={
                isFrameColor
                  ? "bv. Rood, Ocean Blue, Midnight..."
                  : "bv. Race Pro, Carbon Black, Maat 54..."
              }
              className={
                isDark
                  ? "bg-white/[0.04] border-white/[0.06] text-white placeholder-gray-600"
                  : "border-gray-200"
              }
            />
          </div>

          {/* Description */}
          <div className="space-y-2">
            <Label className={isDark ? "text-gray-300" : ""}>
              Beschrijving
            </Label>
            <Textarea
              value={optionForm.description}
              onChange={(e) =>
                setOptionForm((p) => ({
                  ...p,
                  description: e.target.value,
                }))
              }
              placeholder="Extra info over deze optie..."
              rows={2}
              className={
                isDark
                  ? "bg-white/[0.04] border-white/[0.06] text-white placeholder-gray-600"
                  : "border-gray-200"
              }
            />
          </div>

          {/* Hidden file inputs */}
          <input
            ref={optionMainImageInputRef}
            type="file"
            accept="image/*"
            onChange={(e) => onMainImageUpload(e, optionMainImageInputRef)}
            className="hidden"
          />
          <input
            ref={optionSvgInputRef}
            type="file"
            accept=".svg,image/svg+xml"
            onChange={(e) => onSvgUpload(e, optionSvgInputRef)}
            className="hidden"
          />

          {/* FRAME-COLOR SPECIFIC */}
          {isFrameColor && (
            <>
              {/* SVG Mode Active */}
              {optionForm.svgContent && (
                <div className="space-y-2">
                  <Label className={isDark ? "text-gray-300" : ""}>
                    SVG Frame (zone kleuring)
                  </Label>
                  <div
                    className={`relative rounded-lg border p-4 ${isDark ? "border-white/[0.06] bg-white/[0.02]" : "border-gray-200 bg-gray-50"}`}
                  >
                    <div
                      className="w-full flex items-center justify-center [&>svg]:max-w-full [&>svg]:max-h-40 [&>svg]:w-auto [&>svg]:h-auto [&>svg]:block [&>svg]:mx-auto"
                      dangerouslySetInnerHTML={{
                        __html: optionForm.svgContent,
                      }}
                    />
                    <button
                      type="button"
                      onClick={() =>
                        setOptionForm((p) => ({
                          ...p,
                          svgContent: "",
                          zones: [],
                        }))
                      }
                      className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                  <button
                    type="button"
                    onClick={() => optionSvgInputRef.current?.click()}
                    className={`text-xs underline ${isDark ? "text-gray-400 hover:text-white" : "text-gray-500 hover:text-gray-700"}`}
                  >
                    Ander SVG uploaden
                  </button>
                </div>
              )}

              {/* Image Mode Active */}
              {optionForm.image && !optionForm.svgContent && (
                <div className="space-y-2">
                  <Label className={isDark ? "text-gray-300" : ""}>
                    Frame Afbeelding (kleur tinting)
                  </Label>
                  <div
                    className={`relative rounded-lg border p-4 ${isDark ? "border-white/[0.06] bg-white/[0.02]" : "border-gray-200 bg-gray-50"}`}
                  >
                    <div className="w-full flex items-center justify-center overflow-hidden rounded bg-white shadow-md">
                      <TintedImage
                        src={getImageUrl(optionForm.image)}
                        color={optionForm.colorHex || "#ffffff"}
                        color2={optionForm.colorHex2 || undefined}
                        gradientAngle={optionForm.gradientAngle}
                        alt="Frame"
                        className="max-w-full max-h-40 object-contain"
                      />
                    </div>
                    <button
                      type="button"
                      onClick={() =>
                        setOptionForm((p) => ({
                          ...p,
                          image: "",
                        }))
                      }
                      className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                  <button
                    type="button"
                    onClick={() => optionMainImageInputRef.current?.click()}
                    className={`text-xs underline ${isDark ? "text-gray-400 hover:text-white" : "text-gray-500 hover:text-gray-700"}`}
                  >
                    Andere afbeelding uploaden
                  </button>
                </div>
              )}

              {/* Upload Buttons */}
              {!optionForm.svgContent && !optionForm.image && (
                <div className="grid grid-cols-2 gap-3">
                  <button
                    type="button"
                    onClick={() => optionSvgInputRef.current?.click()}
                    className={`h-32 rounded-lg border-2 border-dashed flex flex-col items-center justify-center gap-2 transition-colors ${
                      isDark
                        ? "border-white/10 hover:border-white/20"
                        : "border-gray-300 hover:border-gray-400"
                    }`}
                  >
                    <Upload className="h-5 w-5 text-gray-400" />
                    <span className="text-sm text-gray-400">SVG Frame</span>
                    <span className="text-[10px] text-gray-500 text-center px-2">
                      Zone kleuring per onderdeel
                    </span>
                  </button>
                  <button
                    type="button"
                    onClick={() => optionMainImageInputRef.current?.click()}
                    disabled={uploading}
                    className={`h-32 rounded-lg border-2 border-dashed flex flex-col items-center justify-center gap-2 transition-colors ${
                      isDark
                        ? "border-white/10 hover:border-white/20"
                        : "border-gray-300 hover:border-gray-400"
                    } ${uploading ? "opacity-50 cursor-wait" : ""}`}
                  >
                    <Upload className="h-5 w-5 text-gray-400" />
                    <span className="text-sm text-gray-400">
                      {uploading ? "Uploaden..." : "Afbeelding"}
                    </span>
                    <span className="text-[10px] text-gray-500 text-center px-2">
                      PNG/JPG met kleur tinting
                    </span>
                  </button>
                </div>
              )}

              {/* Zone Configuration */}
              {optionForm.zones.length > 0 && (
                <div className="space-y-2">
                  <Label className={isDark ? "text-gray-300" : ""}>
                    Kleurbare Zones (
                    {optionForm.zones.filter((z) => z.enabled).length}/
                    {optionForm.zones.length})
                  </Label>
                  <p
                    className={`text-xs ${isDark ? "text-gray-400" : "text-gray-500"}`}
                  >
                    Vink aan welke onderdelen de klant mag inkleuren
                  </p>
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {optionForm.zones.map((zone, i) => (
                      <div
                        key={zone.id}
                        className={`flex items-center gap-2.5 p-2.5 rounded-lg border transition-opacity ${
                          zone.enabled
                            ? isDark
                              ? "border-white/10 bg-white/[0.04]"
                              : "border-gray-300 bg-gray-50"
                            : isDark
                              ? "border-white/[0.04] bg-transparent opacity-50"
                              : "border-gray-100 bg-transparent opacity-50"
                        }`}
                      >
                        <button
                          type="button"
                          onClick={() => {
                            const newZones = [...optionForm.zones];
                            newZones[i] = {
                              ...newZones[i],
                              enabled: !newZones[i].enabled,
                            };
                            setOptionForm((p) => ({
                              ...p,
                              zones: newZones,
                            }));
                          }}
                          className="flex-shrink-0"
                        >
                          {zone.enabled ? (
                            <CheckSquare
                              className="h-4 w-4"
                              style={{ color: zone.defaultColor }}
                            />
                          ) : (
                            <Square className="h-4 w-4 text-gray-400" />
                          )}
                        </button>
                        <input
                          type="color"
                          value={zone.defaultColor}
                          onChange={(e) => {
                            const newZones = [...optionForm.zones];
                            newZones[i] = {
                              ...newZones[i],
                              defaultColor: e.target.value,
                            };
                            setOptionForm((p) => ({
                              ...p,
                              zones: newZones,
                            }));
                          }}
                          className="h-7 w-7 rounded cursor-pointer border-0 flex-shrink-0"
                        />
                        <Input
                          value={zone.name}
                          onChange={(e) => {
                            const newZones = [...optionForm.zones];
                            newZones[i] = {
                              ...newZones[i],
                              name: e.target.value,
                            };
                            setOptionForm((p) => ({
                              ...p,
                              zones: newZones,
                            }));
                          }}
                          className={`flex-1 h-8 text-sm ${isDark ? "bg-white/[0.04] border-white/[0.06] text-white" : "border-gray-200"}`}
                        />
                        <span
                          className={`text-[10px] font-mono hidden sm:inline ${isDark ? "text-gray-500" : "text-gray-400"}`}
                        >
                          #{zone.elementId}
                        </span>
                        <button
                          type="button"
                          onClick={() => {
                            setOptionForm((p) => ({
                              ...p,
                              zones: p.zones.filter((_, idx) => idx !== i),
                            }));
                          }}
                          className="p-1 text-gray-400 hover:text-red-500 flex-shrink-0"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}

          {/* STANDARD: Main image (non-frame-color) */}
          {!isFrameColor && (
            <>
              <div className="space-y-2">
                <Label className={isDark ? "text-gray-300" : ""}>
                  Hoofdafbeelding
                </Label>
                {optionForm.image ? (
                  <div className="relative w-fit">
                    <img
                      src={getImageUrl(optionForm.image)}
                      alt="Preview"
                      className={`h-24 w-24 rounded-lg object-cover border ${isDark ? "border-white/[0.06]" : "border-gray-200"}`}
                    />
                    <button
                      onClick={() =>
                        setOptionForm((p) => ({ ...p, image: "" }))
                      }
                      className="absolute -top-1.5 -right-1.5 bg-red-500 text-white rounded-full p-0.5"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => optionMainImageInputRef.current?.click()}
                    disabled={uploading}
                    className={`h-24 w-24 rounded-lg border-2 border-dashed flex flex-col items-center justify-center transition-colors ${isDark ? "border-white/10 hover:border-white/20" : "border-gray-300 hover:border-gray-400"}`}
                  >
                    {uploading ? (
                      <RefreshCw className="h-5 w-5 text-gray-400 animate-spin" />
                    ) : (
                      <>
                        <Upload className="h-5 w-5 text-gray-400" />
                        <span className="text-xs text-gray-400 mt-1">
                          Upload
                        </span>
                      </>
                    )}
                  </button>
                )}
              </div>

              {/* Extra images */}
              <div className="space-y-2">
                <Label className={isDark ? "text-gray-300" : ""}>
                  Extra afbeeldingen
                </Label>
                <input
                  ref={optionExtraImageInputRef}
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={(e) =>
                    onExtraImageUpload(e, optionExtraImageInputRef)
                  }
                  className="hidden"
                />
                <div className="flex flex-wrap gap-2">
                  {optionForm.images.map((img, i) => (
                    <div key={i} className="relative">
                      <img
                        src={getImageUrl(img)}
                        alt={`Extra ${i + 1}`}
                        className={`h-16 w-16 rounded-lg object-cover border ${isDark ? "border-white/[0.06]" : "border-gray-200"}`}
                      />
                      <button
                        onClick={() => removeImage(i)}
                        className="absolute -top-1.5 -right-1.5 bg-red-500 text-white rounded-full p-0.5"
                      >
                        <X className="h-2.5 w-2.5" />
                      </button>
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={() => optionExtraImageInputRef.current?.click()}
                    disabled={uploading}
                    className={`h-16 w-16 rounded-lg border-2 border-dashed flex items-center justify-center transition-colors ${isDark ? "border-white/10 hover:border-white/20" : "border-gray-200 hover:border-gray-300"}`}
                  >
                    {uploading ? (
                      <RefreshCw className="h-4 w-4 text-gray-400 animate-spin" />
                    ) : (
                      <Plus className="h-4 w-4 text-gray-400" />
                    )}
                  </button>
                </div>
              </div>
            </>
          )}

          {/* Price */}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label className={isDark ? "text-gray-300" : ""}>Prijs (€)</Label>
              <Input
                type="number"
                step="0.01"
                value={optionForm.price}
                onChange={(e) =>
                  setOptionForm((p) => ({
                    ...p,
                    price: parseFloat(e.target.value) || 0,
                  }))
                }
                className={
                  isDark
                    ? "bg-white/[0.04] border-white/[0.06] text-white"
                    : "border-gray-200"
                }
              />
            </div>
            <div className="space-y-2">
              <Label className={isDark ? "text-gray-300" : ""}>
                Prijs type
              </Label>
              <Select
                value={optionForm.priceType}
                onValueChange={(val) =>
                  setOptionForm((p) => ({ ...p, priceType: val }))
                }
              >
                <SelectTrigger
                  className={
                    isDark
                      ? "bg-white/[0.04] border-white/[0.06] text-white"
                      : "border-gray-200"
                  }
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent
                  className={
                    isDark ? "bg-[#141414] border-white/[0.06]" : "bg-white"
                  }
                >
                  <SelectItem value="addition">Toeslag (+)</SelectItem>
                  <SelectItem value="fixed">Vaste prijs</SelectItem>
                  <SelectItem value="included">Inbegrepen</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Color */}
          <div className="space-y-2">
            <Label className={isDark ? "text-gray-300" : ""}>
              {isFrameColor ? "Standaard Kleur *" : "Kleur (optioneel)"}
            </Label>
            <div className="flex items-center space-x-3">
              <input
                type="color"
                value={optionForm.colorHex || "#000000"}
                onChange={(e) =>
                  setOptionForm((p) => ({
                    ...p,
                    colorHex: e.target.value,
                  }))
                }
                className="h-10 w-16 rounded cursor-pointer border-0"
              />
              <Input
                value={optionForm.colorHex}
                onChange={(e) =>
                  setOptionForm((p) => ({
                    ...p,
                    colorHex: e.target.value,
                  }))
                }
                placeholder="#000000"
                className={`flex-1 ${isDark ? "bg-white/[0.04] border-white/[0.06] text-white placeholder-gray-600" : "border-gray-200"}`}
              />
              {optionForm.colorHex && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() =>
                    setOptionForm((p) => ({
                      ...p,
                      colorHex: "",
                      colorHex2: "",
                    }))
                  }
                  className={
                    isDark
                      ? "text-gray-400 hover:text-white hover:bg-white/[0.06]"
                      : ""
                  }
                >
                  <X className="h-4 w-4" />
                </Button>
              )}
            </div>

            {/* Gradient */}
            {optionForm.colorHex && (
              <div className="space-y-2 pt-1">
                <div className="flex items-center justify-between">
                  <span
                    className={`text-xs ${isDark ? "text-gray-400" : "text-gray-500"}`}
                  >
                    Gradient
                  </span>
                  <Switch
                    checked={!!optionForm.colorHex2}
                    onCheckedChange={(checked) =>
                      setOptionForm((p) => ({
                        ...p,
                        colorHex2: checked ? p.colorHex : "",
                      }))
                    }
                  />
                </div>
                {optionForm.colorHex2 && (
                  <div className="space-y-2">
                    <div className="flex items-center space-x-3">
                      <input
                        type="color"
                        value={optionForm.colorHex2 || "#000000"}
                        onChange={(e) =>
                          setOptionForm((p) => ({
                            ...p,
                            colorHex2: e.target.value,
                          }))
                        }
                        className="h-10 w-16 rounded cursor-pointer border-0"
                      />
                      <Input
                        value={optionForm.colorHex2}
                        onChange={(e) =>
                          setOptionForm((p) => ({
                            ...p,
                            colorHex2: e.target.value,
                          }))
                        }
                        placeholder="#000000"
                        className={`flex-1 ${isDark ? "bg-white/[0.04] border-white/[0.06] text-white placeholder-gray-600" : "border-gray-200"}`}
                      />
                    </div>
                    <div className="flex items-center gap-2">
                      <span
                        className={`text-xs ${isDark ? "text-gray-400" : "text-gray-500"}`}
                      >
                        Hoek:
                      </span>
                      <input
                        type="range"
                        min="0"
                        max="360"
                        step="15"
                        value={optionForm.gradientAngle ?? 90}
                        onChange={(e) =>
                          setOptionForm((p) => ({
                            ...p,
                            gradientAngle: parseInt(e.target.value),
                          }))
                        }
                        className="flex-1"
                      />
                      <span
                        className={`text-xs font-mono w-8 text-right ${isDark ? "text-gray-400" : "text-gray-500"}`}
                      >
                        {optionForm.gradientAngle ?? 90}°
                      </span>
                    </div>
                    <div
                      className="h-4 w-full rounded"
                      style={{
                        background: `linear-gradient(${optionForm.gradientAngle ?? 90}deg, ${optionForm.colorHex}, ${optionForm.colorHex2})`,
                      }}
                    />
                  </div>
                )}
              </div>
            )}
          </div>

          {/* In Stock */}
          <div className="flex items-center justify-between">
            <div>
              <Label className={isDark ? "text-gray-300" : ""}>
                Op voorraad
              </Label>
              <p
                className={`text-xs ${isDark ? "text-gray-400" : "text-gray-500"}`}
              >
                Niet-beschikbare opties worden getoond maar zijn niet
                selecteerbaar
              </p>
            </div>
            <Switch
              checked={optionForm.inStock}
              onCheckedChange={(checked) =>
                setOptionForm((p) => ({ ...p, inStock: checked }))
              }
            />
          </div>

          {/* Keep Original Size */}
          {(optionForm.image || optionForm.images?.length > 0) && (
            <div className="flex items-center justify-between">
              <div>
                <Label className={isDark ? "text-gray-300" : ""}>
                  Originele grootte behouden
                </Label>
                <p
                  className={`text-xs ${isDark ? "text-gray-400" : "text-gray-500"}`}
                >
                  Voorkomt opschaling van lage-kwaliteit afbeeldingen (minder
                  wazig)
                </p>
              </div>
              <Switch
                checked={optionForm.keepOriginalSize}
                onCheckedChange={(checked) =>
                  setOptionForm((p) => ({
                    ...p,
                    keepOriginalSize: checked,
                  }))
                }
              />
            </div>
          )}

          {/* Dependencies */}
          {editingOptionStepId && (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <div>
                  <Label className={isDark ? "text-gray-300" : ""}>
                    Afhankelijkheden
                  </Label>
                  <p
                    className={`text-xs ${isDark ? "text-gray-400" : "text-gray-500"}`}
                  >
                    Toon deze optie alleen als bepaalde keuzes gemaakt zijn
                  </p>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={addDependency}
                  disabled={
                    getAvailableDependencySteps(editingOptionStepId).length ===
                    0
                  }
                  className={`text-xs ${isDark ? "border-white/[0.06] text-gray-300 hover:bg-white/[0.04]" : ""}`}
                >
                  <Plus className="mr-1 h-3 w-3" />
                  Voorwaarde
                </Button>
              </div>
              {optionForm.dependencies.map((dep, depIndex) => {
                const availableSteps =
                  getAvailableDependencySteps(editingOptionStepId);
                const selectedStep = steps.find((s) => s.id === dep.stepId);
                return (
                  <div
                    key={depIndex}
                    className={`flex items-start space-x-2 p-3 rounded-lg ${isDark ? "bg-white/[0.03]" : "bg-gray-50"}`}
                  >
                    <div className="flex-1 space-y-2">
                      <Select
                        value={dep.stepId}
                        onValueChange={(val) =>
                          updateDependency(depIndex, "stepId", val)
                        }
                      >
                        <SelectTrigger
                          className={`text-sm ${isDark ? "bg-white/[0.04] border-white/[0.06] text-white" : "border-gray-200 bg-white"}`}
                        >
                          <SelectValue placeholder="Kies stap..." />
                        </SelectTrigger>
                        <SelectContent
                          className={
                            isDark
                              ? "bg-[#141414] border-white/[0.06]"
                              : "bg-white"
                          }
                        >
                          {availableSteps.map((s) => (
                            <SelectItem key={s.id} value={s.id}>
                              {s.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {selectedStep && (
                        <>
                          <p
                            className={`text-[10px] ${isDark ? "text-gray-500" : "text-gray-400"}`}
                          >
                            Selecteer bij welke optie(s) deze zichtbaar moet
                            zijn:
                          </p>
                          <div className="flex flex-wrap gap-1.5">
                            {selectedStep.options.map((opt) => (
                              <button
                                key={opt.id}
                                onClick={() => {
                                  const current = dep.optionIds || [];
                                  const newIds = current.includes(opt.id)
                                    ? current.filter((id) => id !== opt.id)
                                    : [...current, opt.id];
                                  updateDependency(
                                    depIndex,
                                    "optionIds",
                                    newIds,
                                  );
                                }}
                                className={`px-2 py-1 text-xs rounded-md border transition-all ${
                                  (dep.optionIds || []).includes(opt.id)
                                    ? isDark
                                      ? "border-white/20 bg-white/[0.06] text-white"
                                      : "border-gray-400 bg-gray-50 text-gray-900"
                                    : isDark
                                      ? "border-white/[0.06] text-gray-400 hover:border-white/10"
                                      : "border-gray-200 text-gray-600 hover:border-gray-300"
                                }`}
                              >
                                {opt.name}
                              </button>
                            ))}
                          </div>
                          {(!dep.optionIds || dep.optionIds.length === 0) && (
                            <p className="text-[10px] text-amber-500 flex items-center gap-1">
                              <AlertCircle className="h-3 w-3" />
                              Geen opties geselecteerd — deze voorwaarde wordt
                              genegeerd
                            </p>
                          )}
                        </>
                      )}
                    </div>
                    <button
                      onClick={() => removeDependency(depIndex)}
                      className="p-1 text-gray-400 hover:text-red-600 mt-1"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            className={
              isDark
                ? "border-white/[0.06] text-gray-300 hover:bg-white/[0.04]"
                : ""
            }
          >
            Annuleren
          </Button>
          <Button
            onClick={onSave}
            className={
              isDark
                ? "bg-white text-black hover:bg-gray-200"
                : "bg-gray-900 text-white hover:bg-gray-800"
            }
            disabled={!optionForm.name.trim()}
          >
            {editingOption ? "Opslaan" : "Toevoegen"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default OptionDialog;
